import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useMediaQuery } from '@mui/material';
import usePersistedState from '../../global/usePersistedState';
import { hasPermissions } from '../../auth/hasPermissions';
import { parseRouteContext, rememberReferenceLabel } from './aiChatEntities';

// Split into three contexts so scenes that only register their page context
// do not re-render when the panel or the page context changes.
const AiContextRegistry = createContext(null);
const AiPageContext = createContext(null);
// Tailwind's sm breakpoint, where Layout switches to the mobile column layout
// (MUI's sm is 600px, which would leave 600-639px without any entry point)
const COMPACT_QUERY = '(max-width:639.95px)';

const AiPanelContext = createContext({
  enabled: false,
  compact: false,
  open: false,
  fullscreen: false,
  setOpen: () => {},
  toggleOpen: () => {},
  setFullscreen: () => {},
});

export const AiChatProvider = ({ children }) => {
  const location = useLocation();
  const enabled = hasPermissions('ai-chat', 'read');
  const compact = useMediaQuery(COMPACT_QUERY);

  const [panel, setPanel] = usePersistedState('aiChat.panel', {
    open: false,
    fullscreen: false,
  });

  // Scenes push their context, the most recently mounted one wins
  const [registered, setRegistered] = useState([]);

  const register = useCallback((context) => {
    const entry = { context };
    setRegistered((prev) => [...prev, entry]);
    return () => setRegistered((prev) => prev.filter((e) => e !== entry));
  }, []);

  const routeContext = useMemo(
    () => parseRouteContext(location.pathname),
    [location.pathname],
  );

  const pageContext = registered.length
    ? registered[registered.length - 1].context
    : routeContext;

  const setOpen = useCallback(
    (open) => setPanel((prev) => ({ ...prev, open })),
    [setPanel],
  );
  const toggleOpen = useCallback(
    () => setPanel((prev) => ({ ...prev, open: !prev.open })),
    [setPanel],
  );
  const setFullscreen = useCallback(
    (fullscreen) => setPanel((prev) => ({ ...prev, fullscreen })),
    [setPanel],
  );

  const panelValue = useMemo(
    () => ({
      enabled,
      compact,
      open: enabled && panel.open,
      fullscreen: panel.fullscreen,
      setOpen,
      toggleOpen,
      setFullscreen,
    }),
    [
      enabled,
      compact,
      panel.open,
      panel.fullscreen,
      setOpen,
      toggleOpen,
      setFullscreen,
    ],
  );

  return (
    <AiContextRegistry.Provider value={register}>
      <AiPageContext.Provider value={pageContext}>
        <AiPanelContext.Provider value={panelValue}>
          {children}
        </AiPanelContext.Provider>
      </AiPageContext.Provider>
    </AiContextRegistry.Provider>
  );
};

/**
 * Tells the AI chat what the user is looking at. Pass null while loading.
 * Outside of the Layout (e.g. standalone apps) this is a no-op.
 *
 * useAiPageContext(apiData?.data && {
 *   type: 'invoice', id: apiData.data.id, label: apiData.data.title,
 *   projectId: apiData.data.project?.id, projectLabel: apiData.data.project?.name,
 * });
 */
export const useAiPageContext = (context) => {
  const register = useContext(AiContextRegistry);
  const serialized = context && context.id ? JSON.stringify(context) : null;

  useEffect(() => {
    if (!register || !serialized) return;
    const parsed = JSON.parse(serialized);
    rememberReferenceLabel(parsed);
    if (parsed.projectId && parsed.projectLabel)
      rememberReferenceLabel({
        type: 'project',
        id: parsed.projectId,
        label: parsed.projectLabel,
      });
    return register(parsed);
  }, [register, serialized]);
};

export const useAiCurrentPageContext = () => useContext(AiPageContext);

export const useAiPanel = () => useContext(AiPanelContext);
